import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import Controls, { InitialControlsDisplay } from "../../../session/editor/moduleControls/controls";
import { updateHomepageModule } from '../../../../../../store/actions/admin/create-event';
import { EventsState } from '../../../../../../store/types';
import { AboutLayoutTypesClassic, TemplateClassNames } from '../../../../../../types/template-layouts';
import { AboutContent, ColorKeys, TextOverrides } from '../../../../../../types/working-model';
import { getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import { UploadImageModal } from "../../../../../general-ui/image-upload-modal/image-upload-modal";
import { addChecklistItem } from '../../../../../../store/actions/authentication';
import { ChecklistTitles } from '../../../../../../store/reducers/admin/assistant';
import { getStylingOverrides } from '../../../../../live-event/utils';
import { EContentColorOverrideNames } from '../../../session/editor/moduleControls/edit-section-colors-modal';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import EditableTextDraft from '../../../../../general-ui/editable-text/editable-text-draft';
import { BasicLangingModuleProps } from '../editor-main';
import '../../../../../live-event/modules/about/about.scss';
import './about.scss';

const EditorAbout: React.FC<BasicLangingModuleProps> = React.forwardRef<HTMLDivElement, BasicLangingModuleProps>(({ page_module, template, language, baseLanguage }, ref) => {
	const {
		AuthReducer: { token, user },
	} = useSelector((event: EventsState) => event);
	const dispatch = useDispatch();
	const [imageModalOpen, setImageModalOpen] = useState(false);

	const content: AboutContent = page_module.content;
	const styling_overrides = page_module?.styling_overrides;
	const {
		backgroundColor,
		backgroundImg,
	} = getStylingOverrides(styling_overrides);
	const layout_type = page_module.content?.layout_type || AboutLayoutTypesClassic.standard;

	function handleTextChange(key: string) {
		return (text: string) => {
			dispatch(
				updateHomepageModule({
					...page_module,
					content: {
						...content,
						[key]: updateTranslateKey({
							translateString: page_module?.content?.[key],
							input: text,
							baseLanguage,
							language
						}),
					},
				})
			);
		};
	}

	function handleStyleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...styling_overrides,
				content: {
					...styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updateHomepageModule({
					...page_module,
					styling_overrides: updatedOverrides
				})
			);
		};
	}

	const saveImage = useCallback((image: { original: string, edited: string; }) => {
		dispatch(
			updateHomepageModule({
				...page_module,
				content: {
					...page_module.content,
					originalImage: image.original,
					image: image.edited,
				}
			})
		);
		// ASSISTANT CHECKLIST ITEM: add assisstant progress checklist item for editing the about section
		if (
			user &&
			!user.onboarded.includes(ChecklistTitles.editAbout)
		) {
			dispatch(addChecklistItem(token!, user.id, user.onboarded, ChecklistTitles.editAbout));
		}
	}, [dispatch, page_module, token, user]);

	// TODO: implement image gallery for about post MVP
	const imageLength = 1;
	const renderGallery = () => {
		const gallery = [];
		for (let i = 0; i < imageLength; i += 1) {
			gallery.push(<img key={i} src={content?.image} alt="" aria-hidden="true" width="275" height="220" />);
		}
		return gallery;
	};

	const { custom_heading, description, title } = getTextValue(content, language);
	const customHeadingDefaultColor = (template === TemplateClassNames.O2)
		? ColorKeys.secondaryBackgroundColor
		: ColorKeys.secondaryAccentColor;

	const controlsDisplay: InitialControlsDisplay = {
		changeSectionPosition: true,
		editColors: true,
		editLayout: true
	};

	return page_module.is_on ? (
		<div
			ref={ref}
			className={classNames(
				'editor-wrapper about-us',
				`about-us-${layout_type}`,
				backgroundColor,
				template
			)}
		>
			<Controls
				module={page_module}
				display={controlsDisplay}
				baseLanguage={baseLanguage}
				language={language}
			/>
			<div className="module-container" style={backgroundImg}>
				<div className="about-us-text">
					<EditableTextSlim
						className="about-us-custom-heading"
						tagType="p"
						value={custom_heading}
						onChange={handleTextChange('custom_heading')}
						colorOverride={styling_overrides?.content?.custom_heading?.color}
						defaultColor={customHeadingDefaultColor}
						elementName={EContentColorOverrideNames.custom_heading}
						onOverrideChange={handleStyleOverrideChange('custom_heading')}
					/>
					<div className="about-us-text-inner">

						<EditableTextSlim
							className="about-us-title"
							tagType="h2"
							value={title}
							onChange={handleTextChange('title')}
							colorOverride={styling_overrides?.content?.title?.color}
							defaultColor={ColorKeys.bodyTextColor}
							elementName={EContentColorOverrideNames.title}
							onOverrideChange={handleStyleOverrideChange('title')}
						/>
						<EditableTextDraft
							className="about-us-description"
							value={description}
							onChange={handleTextChange('description')}
							colorOverride={styling_overrides?.content?.description?.color}
							defaultColor={ColorKeys.bodyTextColor}
							elementName={EContentColorOverrideNames.description}
							onOverrideChange={handleStyleOverrideChange('description')}
						/>
					</div>
				</div>
				<div className={classNames('about-us-gallery-wrapper', `about-us-gallery-wrapper-${imageLength}`)}>
					<div className={classNames('about-us-gallery', `about-us-gallery-${imageLength}`)}>
						{renderGallery()}
					</div>
					<button className="round edit-button" onClick={() => setImageModalOpen(true)}>
						<Icon
							name={ICONS.EDIT}
							size={13}
							color={COLORS.WHITE}
						/>
					</button>
				</div>

			</div>
			<UploadImageModal
				originalImage={content?.originalImage}
				fileTitle={"Image"}
				open={imageModalOpen}
				close={() => setImageModalOpen(false)}
				onSave={saveImage}
			/>
		</div>
	) : null;
});

EditorAbout.displayName = 'EditorAbout';

export default EditorAbout;
